import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { CreditScoreOutlined, CreditCardOffOutlined, DocumentScannerOutlined, ConfirmationNumberOutlined, VpnKeyOutlined } from '@mui/icons-material';
import { PanelLeft, PanelLeftClose } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useKeycloak } from "keycloak-react-web";
import { openParkitNav, closeParkitNav, selectState as selectParkitNav } from './parkitNavSlice';
import { hasKeycloakAuthority } from '../../app/roles';
import { usePermissions } from '../../features/permissions/usePermissions';
import { useFavourites } from '../../features/favourites/useFavourites';
import SharedPinButton from '../../features/favourites/PinButton';
import {
  AnimatedSidebar,
  AnimatedSidebarContent,
  AnimatedSidebarGroup,
  AnimatedSidebarGroupContent,
  AnimatedSidebarGroupLabel,
  AnimatedSidebarHeader,
  AnimatedSidebarMenu,
  AnimatedSidebarMenuButton,
  AnimatedSidebarMenuItem,
  AnimatedSidebarMenuSub,
  AnimatedSidebarMenuSubButton,
  AnimatedSidebarMenuSubItem,
  AnimatedSidebarProvider,
  AnimatedSidebarRail,
  AnimatedSidebarTrigger,
  useAnimatedSidebarPanel,
} from '../ui/animated-sidebar';

import { ReactComponent as AppointmentStatusOnfleetIcon } from '../../assets/icons/appointment_status_onfleet.svg';
import { ReactComponent as OwnerListingsIcon } from '../../assets/icons/owner_listings.svg';
import { ReactComponent as AddParkingBuildingIcon } from '../../assets/icons/add_parking_building.svg';
import { ReactComponent as BuildingEnquiryIcon } from '../../assets/icons/building_enquiry.svg';
import { ReactComponent as ParkingInventoryIcon } from '../../assets/icons/parking_inventory.svg';
import { ReactComponent as MasterlistIcon } from '../../assets/icons/master_list.svg';
import { ReactComponent as DriverEnquiryIcon } from '../../assets/icons/driver_enquiry.svg';
import { ReactComponent as FunnelIcon } from '../../assets/icons/funnel.svg';
import { ReactComponent as TrackingSheetIcon } from '../../assets/icons/tracking_sheet.svg';
import { ReactComponent as TerminationIcon } from '../../assets/icons/termination_request.svg';
import { ReactComponent as BulkEnquiriesIcon } from '../../assets/icons/bulk_enquiries.svg';
import { ReactComponent as ArchivedListingsIcon } from '../../assets/icons/archived_listings.svg';
import { ReactComponent as ArchivedRentingsIcon } from '../../assets/icons/archived_rentings.svg';
import { ReactComponent as InsuranceIcon } from '../../assets/icons/insurance.svg';
import { ReactComponent as DeliveryRateIcon } from '../../assets/icons/delivery_rate.svg';
import { ReactComponent as ParkitUserIcon } from '../../assets/icons/users.svg';
import { ReactComponent as DeleteUserIcon } from '../../assets/icons/delete-user.svg';
import { ReactComponent as AffiliateCampaignIcon } from '../../assets/icons/affiliate_campaign.svg';
import { ReactComponent as PayoutReportIcon } from '../../assets/icons/payout_report.svg';
import { ReactComponent as PayoutIcon } from '../../assets/icons/payout.svg';
import { RateReviewOutlined, DirectionsCarOutlined, AccountBalanceWalletOutlined, RedeemOutlined, CampaignOutlined, MailOutlineOutlined, MenuBookOutlined, HelpOutlineOutlined } from '@mui/icons-material';
import { isEmailCampaignEnabled } from '../../app/utils';

/**
 * Whether one menu item belongs to this person.
 *
 * `can` is passed in rather than read from a hook so that this stays a plain function, callable from
 * a test without rendering anything, and defaults to permitting so that existing callers and the
 * pre-permission tests keep their meaning.
 */
export const isParkitNavPageVisible = (page, keycloak, can = () => true) => {
  if (page.requiredAuthority && !hasKeycloakAuthority(keycloak, page.requiredAuthority)) return false;
  if (page.requiredPermission && !can(page.requiredPermission)) return false;
  if (page.requiredPermissions?.some((permission) => !can(permission))) return false;
  if (page.requiredAnyPermissions && !page.requiredAnyPermissions.some(can)) return false;
  return true;
};

// `requiredPermission` mirrors the route gate in App.js for the same path. They have to agree:
// a visible menu item leading to a refusal is worse than no menu item, and a hidden item whose
// route would have admitted the person is work they cannot find.
//
// At module scope and exported because the top nav needs it too: the ParkIt tab is the only door
// into this sidebar, so it has to know whether there is anything behind it worth opening.
export const parkitMenuGroups = [{
    label: 'Enquiries',
    icon: <DriverEnquiryIcon width={20} height={20} />,
    children: [{
      label: 'Driver Enquiries',
      path: '/parkit/rentings',
      requiredAnyPermissions: ['enquiry.driver:read', 'seasonpass:read'],
      icon: <DriverEnquiryIcon width={20} height={20} />,
    }, {
      label: 'CP Building Enquiries',
      path: '/parkit/building-enquiries',
      requiredPermission: 'enquiry.building:read',
      icon: <BuildingEnquiryIcon width={20} height={20} />,
    }, {
      label: 'Bulk Enquiries',
      path: '/parkit/bulk-enquiries',
      requiredPermission: 'enquiry.bulk:read',
      icon: <BulkEnquiriesIcon width={20} height={20} />,
    }, {
      label: 'LPR Enquiries',
      path: '/parkit/lpr-enquiries',
      requiredPermission: 'enquiry.lpr:read',
      icon: <DocumentScannerOutlined className="text-sidebar-ink-muted" sx={{ fontSize: 20 }} />,
    }, {
      label: 'Engagement',
      path: '/parkit/engagement',
      requiredPermission: 'enquiry.driver:read',
      icon: <FunnelIcon width={20} height={20} />,
    }],
  }, {
    label: 'Subscriptions',
    icon: <CreditScoreOutlined className="text-sidebar-ink-muted" width={20} height={20} />,
    children: [{
      label: 'Subscriptions',
      path: '/parkit/subscriptions',
      requiredPermission: 'subscription:read',
      icon: <CreditScoreOutlined className="text-sidebar-ink-muted" width={20} height={20} />,
    }, {
      label: 'Failed Subscriptions',
      path: '/parkit/failed-subscriptions',
      requiredPermission: 'subscription:read',
      icon: <CreditCardOffOutlined className="text-sidebar-ink-muted" width={20} height={20} />,
    }],
  }, {
    label: 'Listings & Inventory',
    icon: <ParkingInventoryIcon width={20} height={20} />,
    children: [{
      label: 'Parking Areas',
      path: '/parkit/parking-areas',
      requiredPermission: 'parkingarea:read',
      icon: <AddParkingBuildingIcon width={20} height={20} />,
    }, {
      label: 'Season Pass Plans',
      path: '/parkit/season-pass-products',
      requiredPermission: 'product:read',
      icon: <ConfirmationNumberOutlined className="text-sidebar-ink-muted" sx={{ fontSize: 20 }} />,
    }, {
      label: 'Owner Listings',
      path: '/parkit/owner-listings',
      requiredPermission: 'listing.owner:read',
      icon: <OwnerListingsIcon width={20} height={20} />,
    }, {
      label: 'Parking Inventory',
      path: '/parkit/listings',
      requiredPermission: 'listing:read',
      icon: <ParkingInventoryIcon width={20} height={20} />,
    }, {
      label: 'Masterlist',
      path: '/parkit/masterlist',
      requiredAnyPermissions: ['renting:read', 'seasonpass:read'],
      icon: <MasterlistIcon width={20} height={20} />,
    }, {
      label: 'Archived Listings',
      path: '/parkit/archived-listings',
      requiredPermission: 'listing:read',
      icon: <ArchivedListingsIcon width={20} height={20} />,
    }, {
      label: 'Archived Rentings',
      path: '/parkit/archived-rentings',
      requiredPermission: 'renting:read',
      icon: <ArchivedRentingsIcon width={20} height={20} />,
    }, {
      label: 'Archived Season Passes',
      path: '/parkit/archived-season-passes',
      requiredPermission: 'seasonpass:read',
      icon: <ArchivedRentingsIcon width={20} height={20} />,
    }],
  }, {
    label: 'Operations',
    icon: <TrackingSheetIcon width={20} height={20} />,
    children: [{
      label: 'Tracking Sheet',
      path: '/parkit/tracking-rentings',
      requiredAnyPermissions: ['renting:read', 'seasonpass:read'],
      icon: <TrackingSheetIcon width={20} height={20} />,
    }, {
      label: 'Termination Requests',
      path: '/parkit/terminations',
      requiredPermission: 'renting:read',
      icon: <TerminationIcon width={20} height={20} />,
    }, {
      label: 'Season Pass Termination',
      path: '/parkit/season-pass-terminations',
      requiredPermission: 'seasonpass:read',
      icon: <TerminationIcon width={20} height={20} />,
    }, {
      label: 'Appt. Status',
      path: '/parkit/appointment',
      requiredPermission: 'appointment:read',
      icon: <AppointmentStatusOnfleetIcon width={20} height={20} />,
    }, {
      label: 'CPMS Connections',
      path: '/parkit/cpms-connections',
      requiredPermission: 'cpms:read',
      icon: <VpnKeyOutlined className="text-sidebar-ink-muted" sx={{ fontSize: 20 }} />,
    }],
  }, {
    label: 'Customers',
    icon: <ParkitUserIcon className="text-sidebar-ink-muted" width={18} height={18} />,
    children: [{
      label: 'Customer',
      path: '/parkit/customers',
      requiredPermission: 'customer:read',
      icon: <ParkitUserIcon className="text-sidebar-ink-muted" width={18} height={18} />,
    }, {
      label: 'Insurance',
      path: '/parkit/insurance',
      requiredPermission: 'insurance.policy:read',
      icon: <InsuranceIcon width={20} height={20} />,
    }, {
      label: 'Car Insurance',
      path: '/parkit/car-insurance',
      requiredPermission: 'insurance.record:read',
      icon: <DirectionsCarOutlined className="text-sidebar-ink-muted" sx={{ fontSize: 20 }} />,
    }, {
      label: 'Delete User Requests',
      path: '/parkit/delete-requests',
      requiredPermission: 'deletion:read',
      icon: <DeleteUserIcon width={20} height={20} />,
    }],
  }, {
    label: 'Marketing',
    icon: <CampaignOutlined className="text-sidebar-ink-muted" />,
    children: [{
      label: 'Affiliate Campaign',
      path: '/parkit/affiliate-campaigns',
      requiredPermission: 'affiliate:read',
      icon: <AffiliateCampaignIcon width={20} height={20} />,
    }, {
      label: 'Referral Campaign',
      path: '/parkit/referral-campaigns',
      requiredPermission: 'affiliate:read',
      icon: <AffiliateCampaignIcon width={20} height={20} />,
    }, {
      label: 'Referral Claims',
      path: '/parkit/referral-claims',
      requiredPermission: 'affiliate:read',
      icon: <RedeemOutlined className="text-sidebar-ink-muted" sx={{ fontSize: 20 }} />,
    }, {
      label: 'Referral Guide',
      path: '/parkit/referral-guide',
      requiredPermission: 'affiliate:read',
      icon: <MenuBookOutlined className="text-sidebar-ink-muted" sx={{ fontSize: 20 }} />,
    }, {
      label: 'FAQ',
      path: '/parkit/faq',
      requiredPermission: 'chatbot:manage',
      icon: <HelpOutlineOutlined className="text-sidebar-ink-muted" sx={{ fontSize: 20 }} />,
    }, ...(isEmailCampaignEnabled() ? [{
      label: 'Email Campaigns',
      path: '/parkit/email-campaigns',
      requiredPermission: 'campaign:read',
      icon: <MailOutlineOutlined className="text-sidebar-ink-muted" sx={{ fontSize: 20 }} />,
    }] : [])],
  }, {
    label: 'Finance',
    icon: <PayoutIcon width={20} height={20} />,
    children: [{
      label: 'Customer Wallets',
      path: '/parkit/customer-wallets',
      requiredPermissions: ['customer:read', 'wallet:read'],
      icon: <AccountBalanceWalletOutlined className="text-sidebar-ink-muted" sx={{ fontSize: 20 }} />,
    }, {
      label: 'Delivery Rate',
      path: '/parkit/delivery-rate',
      requiredPermission: 'pricing.delivery:read',
      icon: <DeliveryRateIcon width={20} height={20} />,
    }, {
      label: 'Payout Reports',
      path: '/parkit/payout-reports',
      requiredPermission: 'payout.report:read',
      icon: <PayoutReportIcon width={20} height={20} />,
    }, {
      label: 'Payout',
      path: '/parkit/payout',
      requiredPermission: 'payout:read',
      icon: <PayoutIcon width={20} height={20} />,
    }],
  }, {
    label: 'Reviews',
    icon: <RateReviewOutlined className="text-sidebar-ink-muted" sx={{ fontSize: 20 }} />,
    children: [{
      label: 'Reviews',
      path: '/parkit/reviews',
      requiredPermission: 'review:read',
      icon: <RateReviewOutlined className="text-sidebar-ink-muted" sx={{ fontSize: 20 }} />,
    }],
  }];

/**
 * Where the ParkIt tab should send this person, or null if it should not appear.
 *
 * The dashboard when they hold its key, otherwise the first sidebar screen they can reach, otherwise
 * nothing. The middle case is the one that matters: the sidebar only renders inside a ParkIt screen,
 * so a tab pointing at a dashboard they cannot see would take away the entire section from somebody
 * whose actual work is in it — a custom role holding `renting:read` without
 * `dashboard.parkit:read` is a couple of ticks away at any time once the role editor is in use.
 */
export const parkitLandingPath = (keycloak, can = () => true) => {
  if (can('dashboard.parkit:read')) return '/parkit';

  for (const group of parkitMenuGroups) {
    const page = group.children.find((child) => isParkitNavPageVisible(child, keycloak, can));
    if (page) return page.path;
  }
  return null;
};

const PinButton = (props) => {
  const { collapsed } = useAnimatedSidebarPanel();
  if (collapsed) return null;
  return <SharedPinButton {...props} />;
};

const PanelToggle = () => {
  const { collapsed } = useAnimatedSidebarPanel();
  const action = collapsed ? "Expand menu" : "Collapse menu";
  return (
    <AnimatedSidebarTrigger
      aria-label={action}
      title={`${action} (Ctrl/⌘ B)`}
      className="text-sidebar-ink hover:bg-[var(--sidebar-group-bg)]"
    >
      {collapsed ? <PanelLeft className="h-5 w-5" aria-hidden="true" /> : <PanelLeftClose className="h-5 w-5" aria-hidden="true" />}
    </AnimatedSidebarTrigger>
  );
};

const GroupButton = ({ label, icon, expanded, hasActivePage, onToggle }) => {
  const { collapsed } = useAnimatedSidebarPanel();
  return (
    <AnimatedSidebarMenuButton
      icon={icon}
      ariaExpanded={expanded}
      isActive={hasActivePage && (collapsed || !expanded)}
      onSelect={() => onToggle(collapsed)}
    >
      {label}
    </AnimatedSidebarMenuButton>
  );
};

const ParkitNav = () => {
  const { open } = useSelector(selectParkitNav);
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const { can } = usePermissions();
  const { visible, ready, isPinned, togglePin } = useFavourites();
  const [toolbarHeight, setToolbarHeight] = useState(69);
  const currentPath = useLocation().pathname;

  useEffect(() => {
    const measure = () => {
      const toolbar = document.getElementById('appBar');
      if (toolbar) setToolbarHeight(toolbar.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const groups = useMemo(
    () => parkitMenuGroups
      .map((group) => ({ ...group, pages: group.children.filter((page) => isParkitNavPageVisible(page, keycloak, can)) }))
      .filter((group) => group.pages.length > 0),
    [keycloak, can]
  );

  const pinnedPages = useMemo(() => {
    const byPath = new Map(groups.flatMap((group) => group.pages).map((page) => [page.path, page]));
    return visible.map((item) => byPath.get(item.path)).filter(Boolean);
  }, [groups, visible]);
  const pinnedActive = pinnedPages.some((page) => page.path === currentPath);

  const [expandedGroups, setExpandedGroups] = useState(() => Object.fromEntries(
    parkitMenuGroups.map((group) => [group.label, group.children.some((child) => child.path === currentPath)])
  ));

  useEffect(() => {
    const owning = parkitMenuGroups.find((group) => group.children.some((child) => child.path === currentPath));
    if (!owning) return;
    setExpandedGroups((prev) => (prev[owning.label] ? prev : { ...prev, [owning.label]: true }));
  }, [currentPath]);

  const setOpen = useCallback((next) => dispatch(next ? openParkitNav() : closeParkitNav()), [dispatch]);

  return (
    <AnimatedSidebarProvider
      open={open}
      onOpenChange={setOpen}
      className="fixed left-0 z-[1200] w-auto min-h-0"
      style={{
        top: toolbarHeight,
        height: `calc(100vh - ${toolbarHeight}px)`,
        '--sidebar-width': '15rem',
        '--sidebar-width-icon': '4rem',
      }}
    >
      <AnimatedSidebarTrigger
        aria-label="Open menu"
        className="fixed left-2 z-[1200] bg-sidebar text-sidebar-ink shadow-md md:hidden"
        style={{ top: toolbarHeight + 8 }}
      >
        <PanelLeft className="h-5 w-5" aria-hidden="true" />
      </AnimatedSidebarTrigger>

      <AnimatedSidebar ariaLabel="ParkIt navigation" collapsible="icon" className="h-full" panelClassName="h-full">
        <AnimatedSidebarHeader className="flex-row items-center gap-2 border-b border-nav-border px-3 py-2">
          <PanelToggle />
          <span className="truncate text-sm font-semibold text-sidebar-ink group-data-[state=collapsed]/sidebar:hidden">
            ParkIt menu
          </span>
        </AnimatedSidebarHeader>

        <AnimatedSidebarContent className="pb-20">
          {pinnedPages.length > 0 && (
            <AnimatedSidebarGroup role="group" aria-label="Pinned pages">
              <AnimatedSidebarGroupLabel>Pinned</AnimatedSidebarGroupLabel>
              <AnimatedSidebarGroupContent>
                <AnimatedSidebarMenu>
                  {pinnedPages.map((page) => (
                    <AnimatedSidebarMenuItem key={`pinned-${page.path}`} className="group/item">
                      <AnimatedSidebarMenuButton href={page.path} icon={page.icon} isActive={page.path === currentPath} className="pr-10">
                        {page.label}
                      </AnimatedSidebarMenuButton>
                       <PinButton pinned disabled={!ready} label={page.label} onToggle={() => togglePin(page)} />
                    </AnimatedSidebarMenuItem>
                  ))}
                </AnimatedSidebarMenu>
              </AnimatedSidebarGroupContent>
            </AnimatedSidebarGroup>
          )}

          <AnimatedSidebarGroup>
            <AnimatedSidebarGroupLabel>All pages</AnimatedSidebarGroupLabel>
            <AnimatedSidebarGroupContent>
              <AnimatedSidebarMenu>
                {groups.map((group) => {
                  if (group.pages.length === 1) {
                    const [page] = group.pages;
                    return (
                      <AnimatedSidebarMenuItem key={group.label} className="group/item">
                        <AnimatedSidebarMenuButton
                          href={page.path}
                          icon={group.icon}
                          isActive={page.path === currentPath && !pinnedActive}
                          className="pr-10"
                        >
                          {group.label}
                        </AnimatedSidebarMenuButton>
                        <PinButton pinned={isPinned(page.path)} disabled={!ready} label={group.label} onToggle={() => togglePin(page)} />
                      </AnimatedSidebarMenuItem>
                    );
                  }

                  const expanded = !!expandedGroups[group.label];
                  const hasActivePage = group.pages.some((page) => page.path === currentPath);
                  return (
                    <AnimatedSidebarMenuItem key={group.label}>
                      <GroupButton
                        label={group.label}
                        icon={group.icon}
                        expanded={expanded}
                        hasActivePage={hasActivePage && !pinnedActive}
                        onToggle={(collapsed) => setExpandedGroups((prev) => ({ ...prev, [group.label]: collapsed || !prev[group.label] }))}
                      />
                      <AnimatedSidebarMenuSub open={expanded}>
                        {group.pages.map((page) => (
                          <AnimatedSidebarMenuSubItem key={page.path} className="group/item">
                            <AnimatedSidebarMenuSubButton href={page.path} isActive={page.path === currentPath} className="pr-9">
                              {page.label}
                            </AnimatedSidebarMenuSubButton>
                            <PinButton pinned={isPinned(page.path)} disabled={!ready} label={page.label} onToggle={() => togglePin(page)} />
                          </AnimatedSidebarMenuSubItem>
                        ))}
                      </AnimatedSidebarMenuSub>
                    </AnimatedSidebarMenuItem>
                  );
                })}
              </AnimatedSidebarMenu>
            </AnimatedSidebarGroupContent>
          </AnimatedSidebarGroup>
        </AnimatedSidebarContent>

        <AnimatedSidebarRail />
      </AnimatedSidebar>
    </AnimatedSidebarProvider>
  );
};

export default ParkitNav;
